/**
 * Dashboard Layout Component
 * مكون التخطيط المشترك لجميع صفحات لوحة التحكم
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, Bell, Settings, LogOut, Home, ChevronLeft, ChevronRight, Calendar, Clock, Sun
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import LanguageToggle from './LanguageToggle';

// Logo URLs
const LOGO_TRIANGLE = '/brand/ast-logo-asb606.png';
const LOGO_FULL = '/brand/ast-logo-asb606.png';

export default function DashboardLayout({ 
  children, 
  title, 
  titleEn,
  subtitle,
  subtitleEn,
  screenCode, // NEW: Screen code like ASB-001, CGM-001, CGB-001
  showBackButton = true,
  backPath = null, // Changed: default to null so we use navigate(-1)
  onBack = null,
  breadcrumb = []
}) {
  const { logout } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Time and Weather States
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weather, setWeather] = useState({ temp: '31°C', condition: 'sunny' });

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weather data
  useEffect(() => {
    const fetchWeather = async () => {
      try {
        // Dubai coordinates
        const lat = 25.2048;
        const lon = 55.2708;
        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`
        );
        const data = await response.json();
        if (data.current_weather) {
          setWeather({
            temp: `${Math.round(data.current_weather.temperature)}°C`,
            condition: data.current_weather.weathercode < 3 ? 'sunny' : 'cloudy'
          });
        }
      } catch (error) {
        console.log('Weather fetch error:', error);
      }
    };
    fetchWeather();
    // Refresh weather every 30 minutes
    const weatherInterval = setInterval(fetchWeather, 30 * 60 * 1000);
    return () => clearInterval(weatherInterval);
  }, []);

  const displayTitle = language === 'ar' ? title : (titleEn || title);
  const displaySubtitle = language === 'ar' ? subtitle : (subtitleEn || subtitle);
  const isAr = language === 'ar';
  // Full locale + calendar direction follows the selected UI language
  const dateLocale = isAr ? 'ar-AE' : 'en-GB';
  const dir = isAr ? 'rtl' : 'ltr';

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else if (backPath) {
      navigate(backPath);
    } else {
      // Default: go back to previous page
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={dir}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#d4af37]/3 rounded-full blur-[100px]" />
      </div>

      {/* Header - Always shows "لوحة التحكم" */}
      <header className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-2xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-20 flex items-center justify-between">
            {/* Right Side - Logo & Title */}
            <div className="flex items-center gap-4">
              {showBackButton && (
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleBackClick}
                  className="w-11 h-11 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
                  data-testid="back-btn"
                >
                  {isAr
                    ? <ArrowRight className="h-5 w-5 text-[#d4af37]" />
                    : <ArrowLeft  className="h-5 w-5 text-[#d4af37]" />}
                </motion.button>
              )}
              
              {/* Logo - clickable, navigates to dashboard */}
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-3 group cursor-pointer"
                data-testid="header-logo-btn"
                title={language === 'ar' ? 'الصفحة الرئيسية للوحة التحكم' : 'Main dashboard page'}
              >
                <div className="w-14 h-14 flex items-center justify-center">
                  <img 
                    src={LOGO_TRIANGLE}
                    alt="ASB606"
                    className="w-12 h-12 object-contain drop-shadow-[0_0_20px_rgba(212,175,55,0.4)] group-hover:drop-shadow-[0_0_25px_rgba(212,175,55,0.6)] transition-all"
                  />
                </div>
                <div className="text-start">
                  <h1 className="text-xl font-bold bg-gradient-to-r from-[#d4af37] via-[#f3e5ab] to-[#c9a227] text-transparent bg-clip-text">
                    {language === 'ar' ? 'لوحة التحكم' : 'Dashboard'}
                  </h1>
                  <p className="text-xs text-[#71717A] tracking-wider">ASB606 PLATFORMS GROUP</p>
                </div>
              </button>
            </div>
            
            {/* Date, Time & Weather Cards */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Date Card */}
              <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 hover:bg-[#d4af37]/5 hover:border-[#d4af37]/30 transition-all cursor-default min-w-[150px]">
                <div className={`flex flex-col flex-1 ${isAr ? 'items-end' : 'items-start'}`}>
                  <p className="text-white text-sm font-semibold">
                    {currentTime.toLocaleDateString(dateLocale, { weekday: 'long' })}
                  </p>
                  <p className="text-[#A1A1AA] text-[11px]">
                    {currentTime.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                </div>
                <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                  <Calendar className="h-4 w-4 text-emerald-400" />
                </div>
              </div>

              {/* Time Card */}
              <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 hover:bg-[#d4af37]/5 hover:border-[#d4af37]/30 transition-all cursor-default min-w-[140px]">
                <div className={`flex flex-col flex-1 ${isAr ? 'items-end' : 'items-start'}`}>
                  <p className="text-white text-sm font-semibold tabular-nums">
                    {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                  <p className="text-[#A1A1AA] text-[11px]">{isAr ? 'التوقيت المحلي' : 'Local Time'}</p>
                </div>
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center border border-amber-500/20">
                  <Clock className="h-4 w-4 text-amber-400" />
                </div>
              </div>

              {/* Weather Card */}
              <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-xl px-4 py-2.5 hover:bg-[#d4af37]/5 hover:border-[#d4af37]/30 transition-all cursor-default min-w-[110px]">
                <div className={`flex flex-col flex-1 ${isAr ? 'items-end' : 'items-start'}`}>
                  <p className="text-white text-sm font-semibold">{weather.temp}</p>
                  <p className="text-[#A1A1AA] text-[11px]">{isAr ? 'دبي' : 'Dubai'}</p>
                </div>
                <div className="w-9 h-9 rounded-lg bg-yellow-500/10 flex items-center justify-center border border-yellow-500/20">
                  <Sun className="h-4 w-4 text-yellow-400" />
                </div>
              </div>
            </div>

            {/* Left Side - Actions */}
            <div className="flex items-center gap-4">
              {/* Notifications Bell */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard')}
                className="relative w-11 h-11 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
                data-testid="notifications-btn"
              >
                <Bell className="h-5 w-5 text-[#A1A1AA]" />
              </motion.button>
              
              <LanguageToggle className="!h-11 !rounded-xl" />
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/dashboard')}
                className="w-11 h-11 bg-white/[0.03] rounded-xl flex items-center justify-center hover:bg-[#d4af37]/10 transition-all border border-white/10 hover:border-[#d4af37]/40"
                title={language === 'ar' ? 'الصفحة الرئيسية للوحة التحكم' : 'Main dashboard page'}
                data-testid="home-btn"
              >
                <Home className="h-5 w-5 text-[#A1A1AA]" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={logout}
                className="w-11 h-11 bg-red-500/10 rounded-xl flex items-center justify-center hover:bg-red-500/20 transition-all border border-red-500/20 hover:border-red-500/40"
                title={language === 'ar' ? 'تسجيل الخروج' : 'Logout'}
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5 text-red-400" />
              </motion.button>
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <div className="border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1.5 text-sm">
              <button 
                onClick={() => navigate('/dashboard')}
                className="text-[#71717A] hover:text-[#d4af37] transition-colors"
              >
                {isAr ? 'لوحة التحكم' : 'Dashboard'}
              </button>
              {breadcrumb.map((item, index) => {
                // Support both naming conventions: labelAr/labelEn (camelCase) and label_ar/label_en (snake_case)
                const arLabel = item.labelAr ?? item.label_ar ?? '';
                const enLabel = item.labelEn ?? item.label_en ?? '';
                const label = isAr ? arLabel : enLabel;
                const Chevron = isAr ? ChevronLeft : ChevronRight;
                return (
                  <div key={`${item.path || ''}-${enLabel || arLabel || index}`} className="flex items-center gap-2">
                    <Chevron className="h-4 w-4 text-[#71717A] shrink-0" />
                    {item.path ? (
                      <button 
                        onClick={() => navigate(item.path)}
                        className="text-[#71717A] hover:text-[#d4af37] transition-colors whitespace-nowrap"
                      >
                        {label}
                      </button>
                    ) : (
                      <span className="text-[#d4af37] font-medium border-b-2 border-[#d4af37] pb-0.5 whitespace-nowrap">
                        {label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="relative z-10">
        {children}
      </main>

      {/* Footer */}
      <footer className="mt-auto py-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4">
            <img 
              src={LOGO_FULL}
              alt="ASB606"
              className="h-8 object-contain opacity-50"
            />
            <span className="text-[#71717A] text-xs">
              © 2026 ASB606 Platforms Group
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Section Card Component
export function SectionCard({ 
  children, 
  title, 
  titleEn,
  subtitle,
  subtitleEn,
  icon: Icon,
  action,
  className = ""
}) {
  const { language } = useLanguage();
  const displayTitle = language === 'ar' ? title : (titleEn || title);
  const displaySubtitle = language === 'ar' ? subtitle : (subtitleEn || subtitle);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden ${className}`}
    >
      {(displayTitle || action) && (
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            {Icon && (
              <div className="w-10 h-10 rounded-xl bg-[#d4af37]/10 flex items-center justify-center">
                <Icon className="h-5 w-5 text-[#d4af37]" />
              </div>
            )}
            <div>
              <h2 className="text-white font-bold">{displayTitle}</h2>
              {displaySubtitle && (
                <p className="text-xs text-[#71717A]">{displaySubtitle}</p>
              )}
            </div>
          </div>
          {action}
        </div>
      )}
      <div className="p-6">
        {children}
      </div>
    </motion.div>
  );
}

// Stat Card Component
export function StatCard({ 
  value, 
  label, 
  labelEn,
  icon: Icon, 
  trend,
  color = "#d4af37" 
}) {
  const { language } = useLanguage();
  const displayLabel = language === 'ar' ? label : (labelEn || label);

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -4 }}
      className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-5 hover:border-[#d4af37]/30 transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
          <Icon className="h-6 w-6" style={{ color }} />
        </div>
        {trend && (
          <span className={`text-xs font-medium px-2 py-1 rounded-lg ${
            trend > 0 ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
          }`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <p className="text-3xl font-bold text-white mt-4">{value}</p>
      <p className="text-[#71717A] text-sm mt-1">{displayLabel}</p>
    </motion.div>
  );
}

// Action Button Component
export function ActionButton({
  children,
  onClick,
  icon: Icon,
  variant = 'primary', // primary, secondary, danger
  size = 'md', // sm, md, lg
  disabled = false,
  loading = false,
  className = ""
}) {
  const variants = {
    primary: 'bg-[#d4af37] hover:bg-[#e6c353] text-black shadow-[0_0_20px_rgba(212,175,55,0.3)] hover:shadow-[0_0_30px_rgba(212,175,55,0.5)]',
    secondary: 'bg-white/5 hover:bg-white/10 text-white border border-white/10 hover:border-[#d4af37]/40',
    danger: 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20'
  };

  const sizes = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-5 py-2.5 text-sm',
    lg: 'px-6 py-3 text-base'
  };

  return (
    <motion.button
      whileHover={{ scale: disabled ? 1 : 1.02 }}
      whileTap={{ scale: disabled ? 1 : 0.98 }}
      onClick={onClick}
      disabled={disabled || loading}
      className={`
        flex items-center justify-center gap-2 rounded-xl font-bold transition-all
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variants[variant]}
        ${sizes[size]}
        ${className}
      `}
    >
      {loading ? (
        <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : Icon ? (
        <Icon className="h-4 w-4" />
      ) : null}
      {children}
    </motion.button>
  );
}

// Empty State Component
export function EmptyState({ 
  icon: Icon, 
  title, 
  titleEn,
  description, 
  descriptionEn,
  action 
}) {
  const { language } = useLanguage();
  const displayTitle = language === 'ar' ? title : (titleEn || title);
  const displayDescription = language === 'ar' ? description : (descriptionEn || description);

  return (
    <div className="text-center py-16">
      <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#d4af37]/10 flex items-center justify-center">
        <Icon className="h-10 w-10 text-[#d4af37]/50" />
      </div>
      <h3 className="text-xl font-bold text-white mb-2">{displayTitle}</h3>
      <p className="text-[#71717A] max-w-md mx-auto mb-6">{displayDescription}</p>
      {action}
    </div>
  );
}

// Grid Layout Component
export function DashboardGrid({ children, cols = 4 }) {
  const colsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'
  };

  return (
    <div className={`grid ${colsClass[cols]} gap-4`}>
      {children}
    </div>
  );
}

// Navigation Card Component
export function NavCard({ 
  icon: Icon, 
  title, 
  titleEn,
  description, 
  descriptionEn,
  onClick,
  color = "#d4af37",
  badge,
  screenCode, // Screen code badge
  testId, // data-testid for E2E tests
}) {
  const { language } = useLanguage();
  const displayTitle = language === 'ar' ? title : (titleEn || title);
  const displayDescription = language === 'ar' ? description : (descriptionEn || description);

  return (
    <motion.button
      whileHover={{ y: -8, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      data-testid={testId}
      className="group relative w-full bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 text-start cursor-pointer transition-all duration-300 hover:border-[#d4af37]/50 hover:bg-white/[0.04] hover:shadow-[0_8px_32px_rgba(212,175,55,0.12)] overflow-hidden"
    >
      {/* Screen Code Badge */}
      {screenCode && (
        <div className="absolute top-2 left-2 px-2 py-0.5 bg-[#d4af37]/10 rounded-md">
          <span className="text-[10px] font-mono text-[#d4af37]">{screenCode}</span>
        </div>
      )}
      
      {/* Hover Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#d4af37]/0 via-transparent to-[#d4af37]/0 group-hover:from-[#d4af37]/5 group-hover:to-[#d4af37]/5 transition-all duration-500" />
      
      <div className="relative">
        <div className="flex items-start justify-between mb-4">
          <div 
            className="w-14 h-14 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
            style={{ backgroundColor: `${color}15` }}
          >
            <Icon className="h-7 w-7" style={{ color }} />
          </div>
          {badge && (
            <span className="px-2 py-1 bg-[#d4af37] text-black text-xs font-bold rounded-lg">
              {badge}
            </span>
          )}
        </div>
        <h3 className="text-white font-bold text-lg mb-2 group-hover:text-[#d4af37] transition-colors">
          {displayTitle}
        </h3>
        {displayDescription && (
          <p className="text-[#71717A] text-sm">{displayDescription}</p>
        )}
      </div>
    </motion.button>
  );
}
